import { Provider, Message, ProviderResponse, FunctionDefinition } from "../providers/types";
import { makeProvider } from "./simpleAgent";
import { AssetLoader } from "../assets/loader";
import { Persona } from "../types/persona";
import kleur from "kleur";
import { toolRegistry, ExecutionContext, ToolResult } from "../tools/registry";
import { registerBuiltinTools } from "../tools/builtin";
import { summarizeRepo } from "../tools/repo";
import { loadConfig } from "../config";
import { getSessionMemory, SessionMemory } from "../runtime/sessionMemory";
import { getAgentMemory, AgentMemoryManager } from "../runtime/agentMemory";
import { PermissionManager, PermissionMode } from "../permissions";
import * as readline from 'readline';
import { ErrorHandler } from "../errors/errorHandler";

export interface AgentResult {
  type: "completed" | "failed" | "max_iterations";
  content: string;
  iterations: number;
  toolCalls: number;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  context: ExecutionContext;
}

export class ToolCallingAgent {
  private provider: Provider;
  private context: ExecutionContext;
  private persona: Persona | null = null;
  private sessionMemory: SessionMemory;
  private agentMemory: AgentMemoryManager;
  private permissionManager: PermissionManager;

  constructor(sessionId?: string, readlineInterface?: readline.Interface) {
    this.provider = makeProvider();
    
    // Initialize permission system
    const initialMode = process.env.METIS_AUTO_APPROVE === 'true' 
      ? PermissionMode.AUTO_ACCEPT 
      : PermissionMode.NORMAL;
    
    this.permissionManager = new PermissionManager(initialMode, readlineInterface);
    
    this.context = {
      sessionId: sessionId || `session-${Date.now()}`,
      workingDirectory: process.cwd(),
      config: { autoApprove: process.env.METIS_AUTO_APPROVE === 'true' },
      traceEnabled: process.env.METIS_TRACE === 'true',
      verboseEnabled: process.env.METIS_VERBOSE === 'true',
      permissionManager: this.permissionManager
    };

    // Initialize session memory and agent memory
    this.sessionMemory = getSessionMemory(this.context.workingDirectory);
    this.sessionMemory.loadSession(this.context.sessionId);
    
    // Initialize hierarchical agent memory
    this.agentMemory = getAgentMemory(this.context.workingDirectory);

    // Register built-in tools
    registerBuiltinTools();
  }

  async executeWithTools(
    task: string,
    availableTools: string[] = [],
    maxIterations = 10
  ): Promise<AgentResult> {
    
    // Set current task in session memory
    this.sessionMemory.setCurrentTask(task);
    
    if (this.context.verboseEnabled) {
      console.log(`Session: ${this.context.sessionId}`);
      console.log(`Max steps: ${maxIterations}`);
    }

    // Load persona
    await this.loadPersona();

    // Check if provider supports tools
    if (!this.provider.supportsTools()) {
      // Fallback to simple agent behavior
      const { runSimpleAgent } = await import("./simpleAgent");
      const result = await runSimpleAgent("run", task);
      
      // Save to session memory
      this.sessionMemory.addMessage({ role: "user", content: task });
      this.sessionMemory.addMessage({ role: "assistant", content: result });
      
      return {
        type: "completed",
        content: result,
        iterations: 1,
        toolCalls: 0,
        context: this.context
      };
    }

    const tools = this.getAvailableTools(availableTools);
    const systemPrompt = this.buildSystemPrompt(tools);
    
    // Check if we need to auto-compact before building messages
    if (this.sessionMemory.shouldAutoCompact()) {
      if (this.context.verboseEnabled) {
        console.log(kleur.yellow('🗜️  Auto-compacting session context...'));
      }
      
      try {
        // Use intelligent summarization with the provider for better compaction
        await this.sessionMemory.compactSession(async (messages) => {
          if (!this.provider || messages.length === 0) {
            return this.sessionMemory['defaultSummarizer'](messages);
          }
          
          const conversationText = messages.map(m => 
            `${m.role}: ${typeof m.content === 'string' ? m.content : JSON.stringify(m.content)}`
          ).join('\n\n');
          
          const summaryPrompt = `Create a concise summary of this development session, focusing on:
- Key tasks completed and their outcomes
- Important decisions and changes made
- Files and components that were modified
- Current progress and any pending work
- Technical context that should be preserved

Conversation:
${conversationText}

Summary:`;

          try {
            const summary = await this.provider.send([
              { role: 'system', content: 'You create concise, technical summaries of development conversations that preserve important context.' },
              { role: 'user', content: summaryPrompt }
            ]);
            
            return summary;
          } catch (error) {
            console.warn('AI summarization failed, using default:', error.message);
            return this.sessionMemory['defaultSummarizer'](messages);
          }
        });
        
        if (this.context.verboseEnabled) {
          console.log(kleur.green('✅ Session context compacted successfully'));
        }
      } catch (error) {
        if (this.context.verboseEnabled) {
          console.log(kleur.yellow('⚠️  Auto-compact failed, continuing...'));
        }
      }
    }

    // Build messages with session context
    let messages: Message[] = this.buildMessagesWithContext(systemPrompt, task);

    let iterations = 0;
    let toolCalls = 0;
    let totalTokens = { prompt: 0, completion: 0, total: 0 };

    for (let i = 0; i < maxIterations; i++) {
      iterations++;
      
      if (this.context.traceEnabled) {
        console.log(`\n--- Iteration ${i + 1}/${maxIterations} ---`);
        console.log("Messages:", messages.length);
      }

      try {
        const response = await ErrorHandler.withNetworkRetry(
          () => this.provider.sendWithTools(messages, tools, {
            temperature: this.persona?.temperature,
            max_tokens: 2000
          }),
          'AI provider request'
        );

        // Accumulate token usage
        if (response.usage) {
          totalTokens.prompt += response.usage.prompt_tokens;
          totalTokens.completion += response.usage.completion_tokens;
          totalTokens.total += response.usage.total_tokens;
        }

        if (response.type === 'text') {
          // Agent provided a final answer
          
          // Save conversation to session memory
          this.sessionMemory.addMessages(messages);
          
          return {
            type: "completed",
            content: response.content,
            iterations,
            toolCalls,
            tokens: totalTokens,
            context: this.context
          };
        }

        if (response.type === 'tool_call' && response.tool_calls) {
          // Agent wants to call tools
          const assistantMessage: Message = {
            role: "assistant",
            content: response.content,
            tool_calls: response.tool_calls
          };
          messages.push(assistantMessage);

          // Execute each tool call
          for (const toolCall of response.tool_calls) {
            toolCalls++;
            
            if (this.context.traceEnabled) {
              console.log(`🔧 Executing tool: ${toolCall.function.name}`);
            }

            const result = await ErrorHandler.withQuickRetry(
              () => this.executeTool(toolCall),
              `tool execution: ${toolCall.function.name}`
            );
            
            const toolMessage: Message = {
              role: "tool",
              content: JSON.stringify(result),
              tool_call_id: toolCall.id,
              name: toolCall.function.name
            };
            messages.push(toolMessage);

            if (this.context.traceEnabled) {
              console.log(`Tool result: ${result.success ? "✅ Success" : "❌ Failed"}`);
              if (!result.success) {
                console.log(`Error: ${result.error}`);
              }
            }
          }
          
          continue; // Continue the loop for next iteration
        }

      } catch (error: any) {
        console.error(`❌ Error in iteration ${i + 1}:`, error.message);
        return {
          type: "failed",
          content: `Execution failed: ${error.message}`,
          iterations,
          toolCalls,
          tokens: totalTokens,
          context: this.context
        };
      }
    }

    // Max iterations reached
    
    return {
      type: "max_iterations",
      content: "Task did not complete within the maximum number of iterations",
      iterations,
      toolCalls,
      tokens: totalTokens,
      context: this.context
    };
  }

  private async loadPersona(): Promise<void> {
    const personaName = process.env.METIS_PERSONA || 'default';
    const loader = new AssetLoader();
    
    try {
      this.persona = await loader.loadPersona(personaName);
      if (this.context.verboseEnabled) {
        console.log(`Persona: ${this.persona.name}`);
      }
    } catch (error: any) {
      this.persona = {
        name: 'default',
        version: '1.0',
        description: 'Default persona',
        system_prompt: 'You are a helpful coding assistant with access to tools.',
        temperature: 0.2
      };
    }
  }

  private getAvailableTools(requestedTools: string[]): FunctionDefinition[] {
    const allToolNames = toolRegistry.list();
    const toolsToUse = requestedTools.length > 0 ? requestedTools : allToolNames;
    
    const tools: FunctionDefinition[] = [];
    
    for (const toolName of toolsToUse) {
      const tool = toolRegistry.get(toolName);
      if (tool) {
        tools.push({
          name: tool.name,
          description: tool.description,
          parameters: tool.schema || {}
        });
      }
    }

    if (this.context.verboseEnabled) {
      console.log(`Tools: ${tools.length} available`);
    }

    return tools;
  }

  private buildSystemPrompt(tools: FunctionDefinition[]): string {
    const repoSummary = summarizeRepo(60);
    const sessionSummary = this.sessionMemory.getSessionSummary();
    const basePrompt = this.persona?.system_prompt || "You are a helpful coding assistant.";
    
    // Check for Agent.md updates and load hierarchical instructions
    this.agentMemory.refreshIfNeeded();
    const projectInstructions = this.agentMemory.getCurrentProjectInstructions();
    const projectContext = this.agentMemory.generateProjectContext(false);
    
    // Check if we're in planning mode
    const currentMode = this.permissionManager.getCurrentMode();
    const isInPlanningMode = currentMode === 'plan_only';
    
    let systemPrompt = `${basePrompt}

You have access to tools that allow you to read files, write files, check git status, and perform other operations to help complete coding tasks.

Available tools:
${tools.map(tool => `- ${tool.name}: ${tool.description}`).join("\n")}`;

    if (isInPlanningMode) {
      systemPrompt += `

🎯 PLANNING MODE ACTIVE 🎯

You are currently in PLANNING MODE. Your role is to help the user plan their project requirements and create comprehensive Agent.md files.

PLANNING MODE GUIDELINES:
- Focus ONLY on planning and documentation - DO NOT implement actual code
- When the user describes their project, ALWAYS create an Agent.md file using the write_file tool
- Ask clarifying questions to understand project requirements
- Help break down projects into manageable tasks and phases
- Provide architecture guidance and best practices suggestions
- Generate comprehensive Agent.md files with:
  * Project description and context
  * Technical requirements and architecture choices
  * Development guidelines and coding standards
  * Task breakdown and implementation phases
  * Best practices and conventions

AGENT.MD STRUCTURE:
When creating Agent.md files, include:
\`\`\`markdown
# Project: [Project Name]

## Context
[Brief description of what the project does]

## Technical Requirements
- [List key technologies, frameworks, etc.]
- [Database, APIs, external services]
- [Performance and scalability requirements]

## Architecture
[High-level architecture description]

## Development Guidelines
- [Coding standards and conventions]
- [File organization patterns]
- [Testing requirements]
- [Error handling approaches]

## Implementation Phases
1. [Phase 1: Core functionality]
2. [Phase 2: Additional features]
3. [Phase 3: Polish and optimization]

## Tasks Breakdown
- [Specific implementable tasks]
- [Dependencies between tasks]
- [Priority levels]
\`\`\`

IMPORTANT: Always use the write_file tool to create Agent.md files when the user describes their project requirements.

When creating Agent.md files, use EXACTLY this filename: "Agent.md" (capital A, capital M, .md extension).`;
    } else {
      systemPrompt += `

IMPORTANT: Only use tools when the user explicitly asks for coding tasks, file operations, or technical work. For simple greetings, questions, or general conversation, respond normally without using tools.

When the user asks you to implement something or work with code:
1. ANALYZE the request to understand what needs to be done
2. READ relevant files first to understand the current codebase structure  
3. CHECK git status to see what's already changed
4. IMPLEMENT the changes by writing/updating files directly
5. PROVIDE a clear summary of what you accomplished

Use tools appropriately - don't use them for casual conversation or when they're not needed.`;
    }

    systemPrompt += `

TODO MANAGEMENT - IMPORTANT:
You have access to todo management tools (create_todo, update_todo, list_todos, delete_todo, clear_completed_todos).
Use these proactively for ANY task that involves multiple steps or when the user specifically asks for todo/task management.

ALWAYS use todos when:
- User asks to "create a todo" or "make a todo list" or mentions "tasks"
- Multi-step implementation requests
- Complex feature development
- Planning or organizing work

Example workflow:
1. User: "Implement user authentication with login and signup"
2. You: Use create_todo for "Implement login functionality", create_todo for "Implement signup functionality", etc.
3. As you work: Use update_todo to mark items as "in_progress" then "completed"
4. Use list_todos to show progress to user

The user expects you to use these tools when appropriate!

Repository summary:
${repoSummary}`;

    // Add project-specific instructions from Agent.md files
    if (projectInstructions.trim()) {
      systemPrompt += `\n\n=== PROJECT-SPECIFIC INSTRUCTIONS ===\n${projectInstructions}`;
    }
    
    // Add project context information
    if (projectContext.trim()) {
      systemPrompt += `\n\nPROJECT CONTEXT:\n${projectContext}`;
    }

    // Add session context if available
    if (sessionSummary.trim()) {
      systemPrompt += `\n\nSESSION CONTEXT:\n${sessionSummary}`;
    }

    systemPrompt += `\n\nComplete the user's task by taking action with the available tools${isInPlanningMode ? ' (focus on planning and Agent.md creation)' : ''}.`;
    
    return systemPrompt;
  }

  private buildMessagesWithContext(systemPrompt: string, currentTask: string): Message[] {
    const messages: Message[] = [
      { role: "system", content: systemPrompt }
    ];

    // Add recent conversation history for context (but not too much to avoid token overload)
    const recentMessages = this.sessionMemory.getRecentConversation(4); // Last 4 messages
    
    if (recentMessages.length > 0) {
      // Skip system messages from history to avoid duplication
      const conversationHistory = recentMessages.filter(msg => msg.role !== "system");
      messages.push(...conversationHistory);
    }

    // Add current user message
    messages.push({ role: "user", content: currentTask });

    return messages;
  }

  private async executeTool(toolCall: any): Promise<ToolResult> {
    try {
      const params = JSON.parse(toolCall.function.arguments);
      
      const result = await toolRegistry.execute(
        toolCall.function.name,
        params,
        this.context
      );

      // Track file operations in session memory
      this.trackFileOperations(toolCall.function.name, params);

      return result;
    } catch (error: any) {
      return {
        success: false,
        error: `Tool execution failed: ${error.message}`,
        metadata: {
          tool: toolCall.function.name,
          error_type: error.constructor.name
        }
      };
    }
  }

  private trackFileOperations(toolName: string, params: any): void {
    // Track files that are being worked on
    const fileTools = ['read_file', 'write_file', 'edit_file', 'append_to_file'];
    
    if (fileTools.includes(toolName) && params.path) {
      this.sessionMemory.addWorkingFile(params.path);
    } else if (toolName === 'move_file') {
      if (params.from) this.sessionMemory.addWorkingFile(params.from);
      if (params.to) this.sessionMemory.addWorkingFile(params.to);
    }
  }

  // Permission system methods
  getPermissionManager(): PermissionManager {
    return this.permissionManager;
  }

  getCurrentPermissionMode(): PermissionMode {
    return this.permissionManager.getCurrentMode();
  }

  setPermissionMode(mode: PermissionMode): void {
    this.permissionManager.setMode(mode);
  }

  cyclePermissionMode(): PermissionMode {
    return this.permissionManager.cycleMode();
  }

  getPermissionModeDisplay(): string {
    return this.permissionManager.getModeDisplay();
  }

  cleanup(): void {
    this.permissionManager.close();
  }
}